import styled from '@emotion/styled';
import React from 'react';
import { ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { formatAmount, Price } from '@model/common';
import { WarningBox } from '@styles/common';
import { Basket } from '@model/iceberg';
import { useSelector } from 'react-redux';
import { getBasket } from '@state/basket';

/* ***************** *
 *       Types       *
 * ***************** */
interface DiscountPriceProps {
  price: Price;
}

const PriceWrapper = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

const OriginalPrice = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  margin: `0 ${theme.custom.spacing.xSmall}px 0 0`,
  textDecoration: 'line-through',
  fontWeight: 'normal'
}));

const DiscountedPrice = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

const DiscountedPriceDetails = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  fontWeight: 'normal',
  fontSize: '1.4rem',
  margin: `${theme.custom.spacing.xSmall}px 0`
}));

/* *********************
 *    DiscountPrice    *
 * *********************/
export const DiscountPrice = ({ price }: DiscountPriceProps) => {
  const t: any = useI18NextContext();
  const { discount, original, amount, currency } = price;

  return (
    <PriceWrapper>
      {original && <OriginalPrice>{formatAmount(original, currency)}</OriginalPrice>}
      {original && discount && <DiscountedPrice>{formatAmount(amount, currency)}</DiscountedPrice>}
      {discount && (
        <DiscountedPriceDetails>
          {t('tour-product__discount--price-details', {
            discount: formatAmount(discount, currency),
            code: discount / 100
          })}
        </DiscountedPriceDetails>
      )}
    </PriceWrapper>
  );
};

/* *********************
 *    PromoCodeBanner  *
 * *********************/
export const PromoCodeBanner = () => {
  const t: any = useI18NextContext();
  const basket: Basket = useSelector(getBasket);
  const promoCode: string = basket.promoCode;

  return (
    <>
      {promoCode && (
        <WarningBox
          dangerouslySetInnerHTML={{
            __html: t('tour-product__discount--promo-code', { code: `<strong>${promoCode}</strong>` })
          }}
        />
      )}
    </>
  );
};
